/* ==========================================================================
   LSC - Product of the Day: photo parallax (created 2026-10-12, home audit)
   assets/lsc-product-of-the-day-parallax.js

   Moved here unchanged from the inline <script> in
   snippets/lsc-product-of-the-day-parallax.liquid (2 Oct 2026), which
   shipped ~5.7 KB of comment-heavy JS in every home page's HTML. The
   snippet now only emits <script src="..." defer> for this cached file.

   Trade-off: it used to run while the page was parsing, so the photo was
   positioned before first paint. Deferred, it runs just before
   DOMContentLoaded. The section sits far below the fold on the home page
   (and its card image is lazy-loaded there), so the first positioning is
   never on screen. If the section is ever moved to the top of a page, the
   photo may shift by up to half the parallax travel at DOMContentLoaded.

   WHAT IT DOES
   Moves the product card's photo up/down inside its frame as the page
   scrolls, by up to the "Photo parallax" setting (0-30% of the frame
   height in total travel).

   HOW IT WORKS
   - The section root carries data-lsc-potd-parallax="0.15" (15% -> 0.15).
   - On scroll/resize (one requestAnimationFrame per frame, only for sections
     near the viewport), it measures the card's photo frame
     (.product-card__media) and sets ONE custom property on the root:
         --lsc-potd-parallax-y: <px>
     from -travel/2 (frame entering at the bottom of the screen) to
     +travel/2 (frame leaving at the top), so the photo drifts slower than
     the page. travel = setting x frame height.
   - The CSS (assets/lsc-product-of-the-day.css) applies it with the
     standalone `translate` property, so it combines with the "Image zoom"
     `scale` and the theme's hover `transform` instead of overriding them.
     The section also enlarges the photo by (1 + parallax) so the frame
     never shows the photo's edges.
   - The property lives on the root, not the <img>, so it survives the card
     being swapped (browser-side pick, cached card): the new photo inherits
     it immediately.
   - Visitors with "reduce motion" turned on get no movement (and the CSS
     drops the extra enlargement).
   - Every instance on the page is handled by one module (window.LscPotdParallax);
     loading the file twice (two sections) just re-scans.
   - Theme editor: re-rendered sections don't re-run scripts, so the module
     re-scans on shopify:section:load and tidies up on shopify:section:unload
     (listeners are added once, never stacked).
   ========================================================================== */
(function () {
  // Already defined by an earlier Product of the Day section on this page:
  // just pick up the new section.
  if (window.LscPotdParallax) {
    window.LscPotdParallax.scan();
    return;
  }

  var reduceMotion = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  var roots = []; // section roots with parallax > 0
  var visible = new Set(); // the subset currently near the viewport
  var queued = false;

  // Only update sections near the screen. Without IntersectionObserver
  // (very old browsers) every tracked section updates on each frame.
  var io =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (entry.isIntersecting) visible.add(entry.target);
              else visible.delete(entry.target);
            });
            queue();
          },
          { rootMargin: '200px 0px' }
        )
      : null;

  /** Parallax amount from the section setting, as a fraction (0 - 0.3). */
  function amountOf(root) {
    return parseFloat(root.getAttribute('data-lsc-potd-parallax')) || 0;
  }

  /** Position one section's photo for the current scroll position. */
  function update(root) {
    var amount = amountOf(root);
    // Queried every time: the card is replaced when the pick is made in
    // the browser or painted from cache.
    var frame = root.querySelector('[data-lsc-potd-slot="card"] .product-card__media');
    if (!amount || !frame || (reduceMotion && reduceMotion.matches)) {
      root.style.removeProperty('--lsc-potd-parallax-y');
      return;
    }
    var rect = frame.getBoundingClientRect();
    var viewport = window.innerHeight || document.documentElement.clientHeight;
    // 0 = frame just entering at the bottom, 1 = just leaving at the top.
    var progress = (viewport - rect.top) / (viewport + rect.height);
    progress = Math.min(1, Math.max(0, progress));
    var offset = (progress - 0.5) * amount * rect.height;
    root.style.setProperty('--lsc-potd-parallax-y', offset.toFixed(1) + 'px');
  }

  function tick() {
    queued = false;
    (io ? visible : roots).forEach(update);
  }

  function queue() {
    if (queued) return;
    queued = true;
    window.requestAnimationFrame(tick);
  }

  /** Find (new) sections with parallax on and forget removed ones. */
  function scan() {
    roots = roots.filter(function (root) {
      if (root.isConnected) return true;
      if (io) io.unobserve(root);
      visible.delete(root);
      return false;
    });
    document.querySelectorAll('[data-lsc-potd]').forEach(function (root) {
      if (!amountOf(root)) {
        update(root); // clears any offset left from an earlier setting
        return;
      }
      if (roots.indexOf(root) === -1) {
        roots.push(root);
        if (io) io.observe(root);
      }
      update(root); // position immediately, before first paint when inline
    });
  }

  window.addEventListener('scroll', queue, { passive: true });
  window.addEventListener('resize', queue);
  if (reduceMotion && reduceMotion.addEventListener) {
    reduceMotion.addEventListener('change', function () {
      roots.forEach(update);
    });
  }
  document.addEventListener('shopify:section:load', scan);
  document.addEventListener('shopify:section:unload', function () {
    // The section is removed after this event fires; tidy up next frame.
    window.requestAnimationFrame(scan);
  });

  window.LscPotdParallax = { scan: scan, update: update };
  scan();
})();
